import { CalendarDaysIcon } from '@heroicons/react/20/solid';
import { useRouter } from 'next/navigation';
import { FC, Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useContextSelector } from 'use-context-selector';

import { getAssetBookingAvailabilityByDate } from '@/core/apis/asset-manager-apis';
import { quickBuy } from '@/core/apis/order-manager-apis';
import { cartSelector } from '@/core/store/cart/cart-states';
import { CartContext } from '@/core/store/cart/CartProvider';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext, setAppState } from '@/core/store/global/AppProvider';
import { setCartData } from '@/core/store/productContext/product-reducers';
import { productSelector } from '@/core/store/productContext/product-states';
import { TimeSlot } from '@/core/types/cart.type';
import { navigateToOrderSummary } from '@/core/utils/navigation-helpers';
import { appSettings } from '@/settings/app-settings';
import HorizontalDateSelector from '@/ux-legacy/components/common/bentoboard/Components/HorizontalDateSelector';
import {
  addToCart,
  calculateTotal,
  checkAndAssignShareId,
} from '@/ux-legacy/components/common/Cart/CartFunction';
import CheckoutPopup from '@/ux-legacy/components/common/Cart/CheckoutPopup/CheckoutPopup';
import Loader from '@/ux-legacy/components/common/Loader/Loader';
import QuickBuyButton from '@/ux-legacy/experience/detailed/parts/QuickBuyButton';
import UserDetailsForm from '@/ux-legacy/experience/detailed/parts/UserDetailsForm';
import VippsPayButton from '@/ux-legacy/experience/detailed/parts/VippsPayButton';
import OptionsForm from '@/ux-legacy/experience/detailed/ProductBolderAssetShopify/parts/cart-options-form';
import { CartButton } from '@/ux-legacy/uikit/components/components-index';

type BookableProductSelectionPopupProps = {
  data: any;
  vendorConfig?: any;
  screen?: string;
  showCartButtonAlawys?: boolean;
  exisitingLink?: string | undefined;
};

const BookableProductSelectionPopup: FC<BookableProductSelectionPopupProps> = ({
  data,
  vendorConfig,
  screen,
  showCartButtonAlawys = false,
  exisitingLink,
}) => {
  const productState = useContextSelector(AppContext, productSelector);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const dispatch = useContextSelector(AppContext, setAppState);
  const [loading, setLoading] = useState(true);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);

  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [productObj, setProductObj] = useState<any>(null);
  const [price, setPrice] = useState(0);
  const [duration, setDuration] = useState();
  const [peopleCount, setPeopleCount] = useState<any>();
  const [quantity, setQuantity] = useState();
  const [productData, setProductData] = useState<any>();
  const [bookingError, setError] = useState<any>(null);
  const [variantInfo, setVariantInfo] = useState<any>();
  const [addtoCartLoading, setAddToCart] = useState(false);
  const router = useRouter();
  const hooksQtyForm = useForm();
  const hooksForm = useForm();

  const [isVippsPayLoading, setIsVippsPayLoading] = useState(false);
  const [originalPrice, setOriginalPrice] = useState<number | null>(null);
  const authentication = useContextSelector(AppContext, authSelector);
  const [quickBuyProduct, setQuickBuyProduct] = useState<any>();
  const [showUserDetailsForm, setShowUserDetailsForm] = useState<boolean>(false);
  const [isQuickBuyLoading, setIsQuickBuyLoading] = useState<boolean>(false);
  const [showBookingSlide, setShowBookingSlide] = useState<boolean>(false);

  const cartState = useContextSelector(CartContext, cartSelector);
  const setCartState = useContextSelector(CartContext, ([, setCartState]) => setCartState);
  const [showCheckoutPopup, setShowCheckoutPopup] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    if (selectedDate) {
      fetchBookingAvailabilityByDate(selectedDate, variantInfo?.title || data?.versions[0]?.title);
    }
    setProductObj({ product: data });
  }, [selectedDate, variantInfo, data]);

  const fetchBookingAvailabilityByDate = (date: string, duration: number) => {
    if (data?._id) {
      setLoading(true);
      getAssetBookingAvailabilityByDate(appSettings.api, data?._id, date, duration)
        .then((availabilityData) => {
          if (availabilityData) {
            const availableSlotList = availabilityData?.times?.filter((slot: TimeSlot) => slot.capacity >= 1);

            setAvailableSlots(availableSlotList);
            setSelectedSlot(selectedSlot ? selectedSlot : availableSlotList[0]);
          }
        })
        .catch((err) => {
          setAvailableSlots([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const onFormSubmit = async (values: any) => {
    if (data?.status == 2) {
      //inactive product
      setError('Product is Inactive');
      return;
    }

    if (!selectedSlot) {
      setError('Time slot is required');

      setTimeout(() => {
        setError(null);
      }, 1000);
      return;
    }

    if (!bookingError && variantInfo) {
      let qtyObj: any = {};

      setError(null);

      if (variantInfo?.price?.type == 1) {
        qtyObj = { quantity };
      } else {
        qtyObj = {
          peopleCount: {
            adult: peopleCount?.adults || 0,
            child: peopleCount?.children || 0,
          },
        };
      }

      setAddToCart(true);

      try {
        const lineItemPayload: any = {
          assetId: data?._id,
          versionId: variantInfo?._id,
          noOfAdults: qtyObj?.peopleCount?.adult,
          noOfChildren: qtyObj?.peopleCount?.child,
          date: selectedDate,
          time: selectedSlot?.startTime,
          price,
        };

        const responsedata: any = await addToCart(lineItemPayload, variantInfo); //shareId

        if (!responsedata) {
          setAddToCart(false);
          setError(responsedata?.error?.message);
          setTimeout(() => {
            setError(null);
          }, 3000);
        } else {
          setAddToCart(false);
          /* eslint-disable-next-line no-unsafe-optional-chaining */
          const { total, discountAmount, subTotal, taxAmount } = calculateTotal([
            ...productState.dataObj,
            responsedata,
          ]);

          dispatch(
            setCartData({
              /* eslint-disable-next-line no-unsafe-optional-chaining */
              dataObj: [...productState?.dataObj, responsedata],
              currencyCode: responsedata?.currencyCode,
              amount: total, //data.totalAmount,
              subTotal,
              taxAmount,
              discountAmount: discountAmount,
            }),
          );

          setCartState({ ...cartState, open: true });
        }
      } catch (error: any) {
        setAddToCart(false);
        setError(error?.message);
        setTimeout(() => {
          setError(null);
        }, 3000);
      }
    }
  };

  const onQuickPay = async () => {
    setPaymentMethod('QUICK_PAY_VIPPS');

    if (data?.isAddressRequired || data?.isBusinessDetailsRequired || data?.isChildInfoRequired) {
      setShowCheckoutPopup(true);
      return;
    }

    const valid = (await hooksForm.trigger()) && (await hooksQtyForm.trigger());

    if (!valid) {
      return;
    }

    if (data?.status == 2) {
      //inactive product
      setError('Product is Inactive');
      setTimeout(() => {
        setError(null);
      }, 1000);

      return;
    }

    if (!selectedSlot) {
      setError('Time slot is required');

      return;
    }

    let qtyObj: any = {};

    setError(null);

    setIsVippsPayLoading(true);
    if (variantInfo?.price?.type == 1) {
      qtyObj = { quantity };
    } else {
      qtyObj = {
        peopleCount: {
          adult: peopleCount?.adults || 0,
          child: peopleCount?.children || 0,
        },
      };
    }

    const lineItemPayload: any = {
      assetId: data?._id,
      versionId: variantInfo?._id,
      noOfAdults: qtyObj?.peopleCount?.adult,
      noOfChildren: qtyObj?.peopleCount?.child,
      date: selectedDate,
      time: selectedSlot?.startTime,
      price,
      paymentType: 'VIPPS',
    };
    const urlParams = new URLSearchParams(window.location.search);
    const pathName = new URL(window.location.href)?.pathname;
    const shareIdForPurchase = checkAndAssignShareId(urlParams.get('shareId') || undefined);

    if (shareIdForPurchase) {
      lineItemPayload['productShareId'] = shareIdForPurchase;
    }

    if (pathName == '/portal') {
      lineItemPayload['portalId'] = urlParams.get('id');
      lineItemPayload['portalShareId'] = urlParams.get('shareId');
    }

    await quickBuy(lineItemPayload)
      .then((res: any) => {
        window.open(res?.paymentLink, '_self', 'noopener');
      })
      .catch((error: any) => {
        setError(error?.response?.data?.message || error?.message);
        setTimeout(() => {
          setError(null);
        }, 3000);
      })
      .finally(() => {
        setIsVippsPayLoading(false);
      });
  };

  const onQuickBuy = async (collectedUserDetails?: UserDetails) => {
    setPaymentMethod('QUICK_BUY');

    if (data?.isAddressRequired || data?.isBusinessDetailsRequired || data?.isChildInfoRequired) {
      setShowCheckoutPopup(true);
      return;
    }

    let userDetails: {
      name: string;
      phone: string;
      email: string;
      tenantId?: string;
    };
    if (!authentication.loggedIn) {
      if (collectedUserDetails) {
        userDetails = collectedUserDetails;
      } else {
        setShowUserDetailsForm(true);
        setQuickBuyProduct([
          {
            image: data?.thumbnailImage,
            title: data?.name,
            quantity: { key: 'Quantity', value: { quantity } },
            adults: { key: variantInfo?.noOfAdults?.label, value: peopleCount?.adults },
            children: { key: variantInfo?.noOfChildren?.label, value: peopleCount?.children },
            startDate: selectedDate,
            time: selectedSlot?.startTime,
            version: variantInfo,
          },
        ]);
        return;
      }
    } else {
      userDetails = {
        name: authentication?.masterProfile.Name,
        phone: authentication?.masterProfile.Phone,
        email: authentication?.masterProfile.Email,
      };
    }
    userDetails.tenantId = appSettings.client.tenantId;
    const urlParams = new URLSearchParams(window.location.search);
    const valid = (await hooksForm.trigger()) && (await hooksQtyForm.trigger());

    if (!valid) {
      return;
    }

    if (data?.status == 2) {
      //inactive product
      setError('Product is Inactive');
      return;
    }

    if (!selectedSlot) {
      setError('Time slot is required');
      setTimeout(() => {
        setError(null);
      }, 1000);
      return;
    }

    let qtyObj: any = {};

    setError(null);

    setIsVippsPayLoading(true);
    if (variantInfo?.price?.type == 1) {
      qtyObj = { quantity };
    } else {
      qtyObj = {
        peopleCount: {
          adult: peopleCount?.adults || 0,
          child: peopleCount?.children || 0,
        },
      };
    }

    const lineItemPayload: any = {
      assetId: data?._id,
      versionId: variantInfo?._id,
      noOfAdults: qtyObj?.peopleCount?.adult,
      noOfChildren: qtyObj?.peopleCount?.child,
      date: selectedDate,
      time: selectedSlot?.startTime,
      price,
      paymentType: 'FREE',
      userDetails,
    };

    const pathName = new URL(window.location.href)?.pathname;
    const shareIdForPurchase = checkAndAssignShareId(urlParams.get('shareId') || undefined);

    if (shareIdForPurchase) {
      lineItemPayload['productShareId'] = shareIdForPurchase;
    }

    if (pathName == '/portal') {
      lineItemPayload['portalId'] = urlParams.get('id');
      lineItemPayload['portalShareId'] = urlParams.get('shareId');
    }

    await quickBuy(lineItemPayload)
      .then((res: any) => {
        setShowUserDetailsForm(false);
        navigateToOrderSummary(router, res.orderDetails.guid, 'success');
      })
      .catch((error) => {
        setError(error?.response?.data?.message || error?.message);
        setTimeout(() => {
          setError(null);
        }, 3000);
      })
      .finally(() => {
        setIsVippsPayLoading(false);
      });
  };

  const goToProductPage = () => {
    router.push(`/bookable-product/${data?._id}`);
  };

  const getAvailabilityColor = (totalCapacity: number, availableCapacity: number) => {
    const availablePercentage = (availableCapacity / totalCapacity) * 100;

    if (totalCapacity > 1) {
      if (availablePercentage > 50) {
        return 'text-green-500';
      } else if (availablePercentage > 20 && availablePercentage <= 50) {
        return 'text-orange-500';
      } else {
        return 'text-red-500';
      }
    } else {
      return 'text-black';
    }
  };

  const onQuickCheckoutSubmit = async (payload: any) => {
    let userDetails: {
      name: string;
      phone: string;
      email: string;
      tenantId?: string;
    } | null = null;

    if (paymentMethod == 'QUICK_BUY') {
      if (!authentication.loggedIn) {
        userDetails = { ...payload?.userDetails, tenantId: appSettings.client.tenantId };
      } else {
        userDetails = {
          name: authentication?.masterProfile.Name,
          phone: authentication?.masterProfile.Phone,
          email: authentication?.masterProfile.Email,
          tenantId: appSettings.client.tenantId,
        };
      }
    }

    const valid = (await hooksForm.trigger()) && (await hooksQtyForm.trigger());

    if (!valid) {
      return;
    }

    if (data?.status == 2) {
      //inactive product
      setError('Product is Inactive');
      setTimeout(() => {
        setError(null);
      }, 1000);

      return;
    }

    if (!selectedSlot) {
      setError('Time slot is required');

      return;
    }

    let qtyObj: any = {};

    setError(null);

    if (paymentMethod == 'QUICK_BUY') {
      setIsQuickBuyLoading(true);
    } else if (paymentMethod == 'QUICK_PAY_VIPPS') {
      setIsVippsPayLoading(true);
    }

    if (variantInfo?.price?.type == 1) {
      qtyObj = { quantity };
    } else {
      qtyObj = {
        peopleCount: {
          adult: peopleCount?.adults || 0,
          child: peopleCount?.children || 0,
        },
      };
    }

    const lineItemPayload: any = {
      assetId: data?._id,
      versionId: variantInfo?._id,
      noOfAdults: qtyObj?.peopleCount?.adult,
      noOfChildren: qtyObj?.peopleCount?.child,
      date: selectedDate,
      time: selectedSlot?.startTime,
      price,
      paymentType: paymentMethod == 'QUICK_BUY' ? 'FREE' : 'VIPPS',
    };

    if (payload?.participantData?.length > 0) {
      lineItemPayload['participantInfo'] = payload?.participantData;
    }

    if (payload?.addressData?.Line1?.trim()) {
      lineItemPayload['address'] = payload?.addressData || {};
    }

    if (payload?.businessDetails?.name?.trim()) {
      lineItemPayload['businessDetails'] = payload?.businessDetails;
    }

    if (paymentMethod == 'QUICK_BUY') {
      lineItemPayload['userDetails'] = userDetails;
    }

    if (data?.isChildInfoRequired) {
      lineItemPayload['consentForPhotos'] = payload?.consentForPhotos || false;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const pathName = new URL(window.location.href)?.pathname;
    const shareIdForPurchase = checkAndAssignShareId(urlParams.get('shareId') || undefined);

    if (shareIdForPurchase) {
      lineItemPayload['productShareId'] = shareIdForPurchase;
    }

    if (pathName == '/portal') {
      lineItemPayload['portalId'] = urlParams.get('id');
      lineItemPayload['portalShareId'] = urlParams.get('shareId');
    }

    await quickBuy(lineItemPayload)
      .then((res: any) => {
        if (paymentMethod == 'QUICK_BUY') {
          navigateToOrderSummary(router, res.orderDetails.guid, 'success');
        } else if (paymentMethod == 'QUICK_PAY_VIPPS') {
          window.open(res?.paymentLink, '_self', 'noopener');
        }
      })
      .catch((error: any) => {
        setError(error?.response?.data?.message || error?.message);
        setTimeout(() => {
          setError(null);
        }, 3000);
      })
      .finally(() => {
        setShowCheckoutPopup(false);
        setIsVippsPayLoading(false);
        setIsQuickBuyLoading(false);
      });
  };

  return (
    <>
      <div className=" overflow-y-hidden  ">
        <div className="w-full h-full  flex flex-col justify-center min-h-full items-end p-0 md:px-2 text-center sm:items-center mx-auto ">
          <div className="w-full h-full flex flex-col justify-center  relative transform overflow-hidden text-left  ">
            <div className="flex p-4 items-center relative">
              <HorizontalDateSelector
                onSelectDate={(date) => {
                  setSelectedDate(date);
                }}
                nextAvailableDate={data.availability?.date || ''}
                productId={data?._id}
              />
            </div>
            <div className="w-full text-right px-4">
              <button className="text-blue-500 hover:underline font-bold" onClick={goToProductPage}>
                <CalendarDaysIcon className="h-6 w-6" />
              </button>
            </div>
            {loading && (
              <div className="flex justify-center items-center">
                <Loader />
              </div>
            )}

            <div className="grow min-h-[250px] overflow-y-scroll overflow-x-hidden custom-scrollbar-invisible">
              {!loading && (
                <div className="flex flex-row flex-wrap justify-center gap-1 gap-y-3 gap-x-2 p-4 md:max-h-[250px] overflow-y-auto">
                  {availableSlots.map(
                    (slot: TimeSlot, index: number) =>
                      slot.capacity > 0 && (
                        <button
                          key={slot.startTime}
                          className={`p-1 rounded-md text-center bg-gray-200 h-[50px] w-[80Px] md:h-[55px] ${
                            selectedSlot?.startTime == slot.startTime ? 'border' : 'border-0'
                          } ${getAvailabilityColor(slot?.totalCapacity, slot?.capacity)}`}
                          style={{ borderColor: 'black' }}
                          onClick={() => {
                            setSelectedSlot(slot);
                          }}
                        >
                          {`${slot.startTime}`}
                          {slot?.totalCapacity > 1 && <div className="text-xs">{`(${slot.capacity})`}</div>}
                        </button>
                      ),
                  )}
                </div>
              )}

              {availableSlots?.length <= 0 && !loading && (
                <div className="p-4 flex justify-center items-center">Slots not found</div>
              )}

              {availableSlots?.length > 0 && (
                <div className="w-full m-2 grow-0 p-4">
                  <OptionsForm
                    assetManagerData={productObj}
                    setPrice={setPrice}
                    setVariant={setVariantInfo}
                    setDuration={setDuration}
                    setPeopleCount={setPeopleCount}
                    setQuantity={setQuantity}
                    setMessageError={setError}
                    hooksForm={hooksForm}
                    hooksQtyForm={hooksQtyForm}
                    maxCapacity={selectedSlot?.capacity || 0}
                    originalPrice={setOriginalPrice}
                    // itemData={itemData}
                  />
                </div>
              )}
            </div>

            {addtoCartLoading && (
              <div className="animate-pulse flex p-2">
                <div className="text-[#3116a988] text-lg font-bold mb-2">Adding to cart, please wait ...</div>
              </div>
            )}
            {bookingError && <div className="flex text-red-500 p-2">{bookingError}</div>}
            <hr />

            <div className="flex flex-row-reverse gap-2 p-4">
              <CartButton
                classNames="h-[48px]"
                disabled={
                  productState?.dataObj?.length
                    ? productState?.dataObj[0]?.vendorId != data?.vendor?._id
                    : false
                }
                onClick={async (event: any) => {
                  event.stopPropagation();
                  const valid = (await hooksForm.trigger()) && (await hooksQtyForm.trigger());
                  if (valid) {
                    setError(null);
                    hooksForm.handleSubmit(onFormSubmit)(event);
                  }
                }}
              />
              {price <= 0 && selectedSlot ? (
                <QuickBuyButton classNames="h-[48px]" onBuy={() => onQuickBuy()} buttonLabel="Book" />
              ) : (
                <></>
              )}
              {vendorConfig?.vipps && price > 0 && selectedSlot && (
                <VippsPayButton classNames="h-[48px]" isLoading={isVippsPayLoading} onClick={onQuickPay} />
              )}
              <div
                className={[
                  'px-4 md:h-auto w-full md:w-auto flex flex-auto flex-col pr-4 space-y-1 justify-center md:justify-start',
                  !price ? '' : '',
                ].join(' ')}
              >
                {price && price > 0 && selectedSlot?.startTime ? (
                  <>
                    <div className="h-full text-[23px] text-[#323232] font-medium flex flex-row justify-start items-center">
                      {`${price} ${data?.vendor?.currencyCode || ''}`}
                      {originalPrice && originalPrice > 0 && (
                        <div className="flex ml-2 text-[23px] text-gray-500">
                          <p className="mr-1 line-clamp-1" style={{ textDecoration: 'line-through' }}>
                            {originalPrice} {data?.vendor?.currencyCode}
                          </p>
                          <p>({data?.discountPercentage}% Off)</p>
                        </div>
                      )}
                    </div>
                    {/* <div className="h-[2px] w-full border-b border-b-[#CECECE] hidden md:block"></div> */}
                    {/* <div className="text-[18px] lg:text-[23px] text-[#323232] flex flex-row md:justify-end">
                        {price ? `${itemData?.currencyCode || ''}${price}` : 'Free'}
                      </div> */}
                  </>
                ) : (
                  <div className="h-full text-[23px] text-[#323232] font-medium flex flex-row justify-start items-center">
                    <span className="flex items-center">
                      {/* {product?.price
                              ? `${product?.price} ${product?.vendor?.currencyCode || ''}`
                              : 'Free'}
                            {originalPrice && originalPrice > 0 && (
                              <div className="flex ml-2 text-[23px] text-gray-500">
                                <p className="mr-1 line-clamp-1" style={{ textDecoration: 'line-through' }}>
                                  {originalPrice} {product?.vendor?.currencyCode}(
                                  {product?.discountPercentage}% Off)
                                </p>
                              </div>
                            )} */}
                      {data?.price ? `${data?.price.toFixed(2)} ${data?.vendor?.currencyCode || ''}` : 'Free'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showUserDetailsForm && (
          <UserDetailsForm
            title="Book"
            onClose={() => setShowUserDetailsForm(false)}
            onSubmit={(details) => {
              onQuickBuy(details);
            }}
            isLoading={isQuickBuyLoading}
            products={quickBuyProduct}
          />
        )}
      </div>

      {showCheckoutPopup && (
        <CheckoutPopup
          onSubmit={onQuickCheckoutSubmit}
          isSubmitInProgress={isQuickBuyLoading || isVippsPayLoading}
          isUserDetailsRequired={!authentication?.loggedIn && paymentMethod == 'QUICK_BUY'}
          enabledStages={{
            isAddressRequired: data?.isAddressRequired,
            isBusinessDetailsRequired: data?.isBusinessDetailsRequired,
            isChildInfoRequired: data?.isChildInfoRequired,
          }}
          show={showCheckoutPopup}
          onClose={() => {
            setShowCheckoutPopup(false);
          }}
          // participantCount={(peopleCount?.adults || 0) + (peopleCount?.children || 0)}
          participantCountData={[
            {
              assetId: data?._id,
              name: data?.name,
              count: (peopleCount?.adults || 0) + (peopleCount?.children || 0),
            },
          ]}
        />
      )}
    </>
  );
};

export default BookableProductSelectionPopup;
